import React, { useState } from "react";
// react redux
import { useSelector } from "react-redux";
// components
import StatisticsCard from "../../components/cards/StatisticsCard";
import PageWrapper from "../../components/page/PageWrapper";
import PageTitle from "../../components/page/PageTitle";
import FiltersContainer from "../../components/filters/FiltersContainer";
import FromDateFilter from "../../components/filters/FromDateFilter";
import ToDateFilter from "../../components/filters/ToDateFilter";
import ErrorSection from "../../components/alerts/ErrorSection";
// mui
import { Skeleton, Stack } from "@mui/material";
// utils
import { RANDOM_COLORS } from "../../utils/RandomColors";
// fetching
import { useQuery } from "@tanstack/react-query";
import fetching from "../../helpers/fetching/fetching";
// hooks
import useFilterParams from "../../hooks/useFilterParams";
// types
import type { UserState } from "../../types/types";
// utils
import fireAlert from "../../helpers/alerts/fireAlert";
// icons
import { GoGitPullRequest } from "react-icons/go";
import { FaCheck } from "react-icons/fa6";
import { MdDoNotDisturb, MdOutlineAttachMoney } from "react-icons/md";

type Stats = {
  query: {
    from: string;
    to: string;
  };
  total_accepted: number;
  total_accepted_fee: number;
  total_forms: number;
  total_rejections: number;
};

const MarketerHome = () => {
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const { from, to } = useFilterParams();

  // {"query":{"from":"0001-01-01T00:00:00Z","to":"2024-12-21T17:16:14.521601523Z"},"total_users":41,"total_businesses":54,"total_categories":44,"total_faqs":3,"total_tags":51,"total_cities":24,"total_empty_search":241,"total_notifications":75,"total_suggestions":14}

  const getStatistics = () => {
    let endpoint = "/statistics/marketer?";
    if (to) endpoint += "to=" + to;
    if (to && from) endpoint += "&";
    if (from) endpoint += "from=" + from;
    return fetching(endpoint, userToken)
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          throw data.message || "Error";
        }
        return data;
      })
      .catch((err) => fireAlert(err.toString(), "error"));
  };

  const {
    data: stats,
    isLoading,
    isError,
    error,
  } = useQuery<Stats, any>({
    queryKey: ["marketer-stats", from, to],
    queryFn: getStatistics,
  });

  return (
    <PageWrapper>
      <PageTitle title="الرئيسية" />
      <FiltersContainer>
        <FromDateFilter />
        <ToDateFilter />
      </FiltersContainer>
      {isLoading ? (
        <Stack
          direction="row"
          gap="16px"
          justifyContent="center"
          flexWrap="wrap"
        >
          {Array(3)
            .fill(0)
            .map((item) => (
              <Skeleton
                height="72px"
                width="266px"
                sx={{ transform: "scale(1,1)" }}
              />
            ))}
        </Stack>
      ) : isError ? (
        <ErrorSection errorMsg={error.toString()} />
      ) : (
        <Stack
          direction="row"
          gap="16px"
          justifyContent="center"
          flexWrap="wrap"
        >
          <StatisticsCard
            color={RANDOM_COLORS[0]}
            title=" عدد طلبات الانضمام"
            Icon={GoGitPullRequest}
            value={stats?.total_forms ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[5]}
            title="عدد الطلبات المقبولة "
            Icon={FaCheck}
            value={stats?.total_accepted ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[2]}
            title="عدد الطلبات المرفوضة"
            Icon={MdDoNotDisturb}
            value={stats?.total_rejections ?? 0}
          />
          <StatisticsCard
            color={RANDOM_COLORS[3]}
            title="الدخل المالي"
            Icon={MdOutlineAttachMoney}
            value={stats?.total_accepted_fee ?? 0}
          />
        </Stack>
      )}
    </PageWrapper>
  );
};

export default MarketerHome;
